import { useEffect, useState } from 'react';
import { Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import type { ServiceOrder, ServiceWo } from '../../api/types';
import { fetchServiceWo } from '../../api/service';
import { t } from '../../lib/i18n';

export interface WoTotals { labor: number; partsSell: number; partsCost?: number; total: number; gp?: number; gpPct?: number; }
export function woTotals(wo: ServiceWo): WoTotals {
  const tt = wo.totals;
  return { labor: tt.labor_mxn, partsSell: tt.parts_sell_mxn, partsCost: tt.parts_cost_mxn, total: tt.total_mxn, gp: tt.gp_mxn, gpPct: tt.gp_pct };
}

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const fmtHrs = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export default function Orden360({ sel, orders, onPick }: { sel: { sid: number; tnum: number } | null; orders: ServiceOrder[]; onPick: (sid: number, tnum: number) => void }) {
  const [wo, setWo] = useState<ServiceWo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { if (!sel) { setWo(null); return; } let a = true; setWo(null); setErr(null);
    fetchServiceWo(sel.sid, sel.tnum).then((r) => a && setWo(r)).catch((e: Error) => a && setErr(e.message)); return () => { a = false; }; }, [sel]);
  const c = t.service.cockpit;
  if (!sel) {
    const list = [...new Map(orders.map((o) => [`${o.sid}-${o.tnum}`, o])).values()].slice(0, 40);
    return (<Card><Text>{t.service.ordersTitle}</Text>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
        {list.map((o) => <button key={`${o.sid}-${o.tnum}`} className="text-left text-sm underline" onClick={() => onPick(o.sid, o.tnum)}>#{o.tnum} · {o.anum_name}</button>)}
      </div></Card>);
  }
  if (err) return <Card><Text>{err}</Text></Card>;
  if (!wo) return <Card><Text>…</Text></Card>;
  const tot = woTotals(wo);
  return (
    <div className="space-y-4">
      <Card>
        <Title className="text-base">#{wo.header.tnum} · {wo.header.anum_name}</Title>
        <Text className="text-sm">{wo.header.equip_desc} · {wo.header.equip_plate} · {c.writer}: {wo.header.writer || '—'} · {c.promise}: {wo.header.promise_date || '—'}</Text>
      </Card>
      <Card>
        <Title className="text-base">{c.laborLines}</Title>
        <Table className="mt-2"><TableHead><TableRow>
          <TableHeaderCell>{t.service.techCols.tech}</TableHeaderCell>
          <TableHeaderCell>Trabajo</TableHeaderCell>
          <TableHeaderCell className="text-right">{t.service.techCols.estHrs}</TableHeaderCell>
          <TableHeaderCell className="text-right">{t.service.techCols.actHrs}</TableHeaderCell>
          <TableHeaderCell className="text-right">{t.service.techCols.revenue}</TableHeaderCell>
        </TableRow></TableHead><TableBody>
          {wo.labor.map((l) => (<TableRow key={l.seq}>
            <TableCell>{l.tech_name || l.tech}</TableCell><TableCell>{l.job_desc || l.job_code}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtHrs.format(l.est_hrs)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtHrs.format(l.act_hrs)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtMxn.format(l.price_mxn)}</TableCell>
          </TableRow>))}
        </TableBody></Table>
      </Card>
      <Card>
        <Title className="text-base">{c.partsLines}</Title>
        <Table className="mt-2"><TableHead><TableRow>
          <TableHeaderCell>Parte</TableHeaderCell>
          <TableHeaderCell className="text-right">Cant.</TableHeaderCell>
          <TableHeaderCell className="text-right">Venta</TableHeaderCell>
          {wo.parts.some((p) => p.gp_mxn !== undefined) && <TableHeaderCell className="text-right">{c.gp}</TableHeaderCell>}
        </TableRow></TableHead><TableBody>
          {wo.parts.map((p, i) => (<TableRow key={i}>
            <TableCell>{p.pdesc || p.part}</TableCell>
            <TableCell className="text-right tabular-nums">{p.qty_sold}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtMxn.format(p.ext_sell_mxn)}</TableCell>
            {p.gp_mxn !== undefined && <TableCell className="text-right tabular-nums">{fmtMxn.format(p.gp_mxn)}</TableCell>}
          </TableRow>))}
        </TableBody></Table>
      </Card>
      <Card className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label={c.laborLines} value={fmtMxn.format(tot.labor)} />
        <Kpi label={c.partsLines} value={fmtMxn.format(tot.partsSell)} />
        <Kpi label={t.service.cols.revenue} value={fmtMxn.format(tot.total)} />
        {tot.gpPct !== undefined && <Kpi label={c.gpPct} value={`${tot.gpPct}%`} />}
      </Card>
    </div>
  );
}
function Kpi({ label, value }: { label: string; value: string }) {
  return (<div><div className="text-tremor-content text-xs">{label}</div>
    <div className="text-tremor-content-strong text-lg font-semibold tabular-nums">{value}</div></div>);
}
